import clsx from 'clsx'
import type { Dispatch, PropsWithChildren, SetStateAction } from 'react'
import React, { useState } from 'react'
import { useId } from '../hooks/useId'
import { CheckboxContext } from './CheckboxContext'
import type { StateDefinition } from './CheckboxContext'

type GroupDirection = 'row' | 'col'

export interface CheckboxGroupProps {
  /**
   * If a visible label isn't specified for a CheckboxGroup, an aria-label must
   * be provided for accessibility
   */
  ariaLabel?: string
  /**
   * The layout direction of the checkbox group.
   * Use `row` for horizontal, `col` for vertical
   * @default 'col'
   */
  direction?: GroupDirection
  /**
   * Whether the checkbox group is in an error state
   * @default false
   * */
  error?: boolean
  /**
   * Additional descriptive text or error text to display below the checkbox group
   * @default ''
   */
  helperText?: string
}

function Group({
  children,
  ariaLabel,
  error = false,
  helperText = '',
  direction = 'col',
}: PropsWithChildren<CheckboxGroupProps>): JSX.Element {
  const id = useId()
  const groupId = `coconut-checkbox-group-${id}`
  const [labelledBy, setLabelledBy] = useState<string | undefined>()

  const contextValue: [
    StateDefinition,
    Dispatch<SetStateAction<string | undefined>>,
  ] = [
    {
      error,
      groupId,
      describedBy: helperText ? `${groupId}-helper-text` : undefined,
      labelledBy,
    },
    setLabelledBy,
  ]

  return (
    <CheckboxContext.Provider value={contextValue}>
      <div>
        <div
          {...(labelledBy
            ? { 'aria-labelledby': labelledBy }
            : ariaLabel
            ? { 'aria-label': ariaLabel }
            : {})}
          className={clsx(
            `flex flex-${direction}`,
            direction === 'row' ? 'items-center' : '',
          )}
          role="group"
        >
          {children}
        </div>
        {helperText ? (
          <span
            aria-live={error ? 'assertive' : 'off'}
            className={clsx(
              'text-xs',
              error ? 'text-error-main' : 'text-black-60',
            )}
            id={`${groupId}-helper-text`}
          >
            {helperText}
          </span>
        ) : null}
      </div>
    </CheckboxContext.Provider>
  )
}

export default Group
